import { VoiceStatus } from "@next-platform/packages-ts-types";
import { Button, ButtonSize, ButtonVariant, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useAppDispatch, useTranslatedVoiceSampleScript, useUserVoices } from "@next-platform/client-common-core";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Col, GenerateSampleSection, Row, Spinner } from "@/components";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";

import useVoicePreview from "./useVoicePreview";

function VoiceClonePreview() {
  const { cloneId } = useParams();
  const { getTranslatedVoiceSampleScript } = useTranslatedVoiceSampleScript();
  const dispatch = useAppDispatch();
  const { getUserVoiceById, isLoading, voice, approveUserVoice, isSuccess, setIsSuccess } = useUserVoices();
  const sampleText = getTranslatedVoiceSampleScript(voice?.language);

  const { previewVoice, stopPreview, isGenerating, isPlaying } = useVoicePreview(voice?.id);

  const navigate = useNavigate();
  const voiceId = voice?.id ?? "";
  const isReadyForPreview = voice?.status === VoiceStatus.READY_FOR_PREVIEW;

  const handleSpeak = useCallback(() => void previewVoice(sampleText), [previewVoice, sampleText]);
  const handleStop = useCallback(() => void stopPreview(), [stopPreview]);

  const onBackClick = useCallback(() => navigate(`${ROUTES.voiceCreate}?recloneId=${cloneId}`), [navigate, cloneId]);

  const onSubmit = useCallback(() => {
    if (voiceId) void approveUserVoice({ voice_id: voiceId, preview_sample: sampleText });
  }, [approveUserVoice, sampleText, voiceId]);

  // Fetch user voices only until we get real API calls
  useEffect(() => {
    if (cloneId) {
      void getUserVoiceById(cloneId);
    }
  }, [cloneId, getUserVoiceById]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setIsSuccess(false));
      navigate("/my-creations/voices");
    }
  }, [isSuccess, navigate, dispatch, setIsSuccess]);

  if (isLoading) {
    return <Spinner centerToScreen={true} />;
  }

  return (
    <Row className="w-full flex flex-col max-w-[700px] gap-lg mx-auto">
      <Row>
        <Col>
          <NavigateBackButtonV2 />
        </Col>
      </Row>
      <Col className="border border-solid border-gray-7 rounded-2xl p-lg gap-y-lg bg-gray-1 dark:bg-gray-3">
        <div>
          <Text as="h5" className="mb-1" variant={TextVariants.HEADING_5_BOLD}>
            <Trans i18nKey="voice:form.preview.sample.title" />
          </Text>
          <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-10">
            <Trans i18nKey="voice:form.preview.sample.subtitle" />
          </Text>
        </div>
        <GenerateSampleSection sampleText={sampleText} isPlaying={isPlaying} isLoading={isGenerating} onSpeak={handleSpeak} onStop={handleStop} />
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-10">
          <Trans i18nKey="voice:form.preview.sample.footer" />
        </Text>
        {isReadyForPreview ? (
          <div className="flex flex-col md:flex-row gap-2 justify-end">
            <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.LARGE} onClick={onBackClick}>
              <Trans i18nKey="voice:form.preview.cta.back" />
            </Button>
            <Button
              size={ButtonSize.LARGE}
              type="submit"
              onClick={onSubmit}
              aria-labelledby="create-and-chat"
              loading={isLoading}
              loadingWithText={true}
              disabled={false}
            >
              <Trans i18nKey="voice:form.preview.cta.submit" />
            </Button>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}
export default VoiceClonePreview;
